import { httpsCallable } from "firebase/functions";

import { functions } from "../firebase/client";

type AiRoute =
  | "generateAtlasAnalysis"
  | "generateAtlasImage"
  | "analyzeMedicalReport"
  | "generateSurgicalPlan"
  | "generateBannerAI"
  | "searchOrthopedicGuidelines"
  | "generateContent";

const FUNCTION_BY_ROUTE: Record<AiRoute, string> = {
  generateAtlasAnalysis: "generateAtlasAnalysis",
  generateAtlasImage: "generateAtlasImage",
  analyzeMedicalReport: "analyzeMedicalReport",
  generateSurgicalPlan: "generateSurgicalPlan",
  generateBannerAI: "generateBannerAI",
  searchOrthopedicGuidelines: "searchOrthopedicGuidelines",
  generateContent: "generateGeminiContent",
};

const DEFAULT_ATLAS_LANGUAGE = "pt";
const LANGUAGE_RETRY_FLAG = "__atlasLanguageRetry";
const LANGUAGE_RETRY_DISABLED_FLAG = "__atlasDisableLanguageRetry";
const ATLAS_PROMPT_VERSION = "atlas-pipeline-2026-04-29";

const normalizeAtlasLanguage = (value: unknown) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "en" || normalized === "es" || normalized === "pt") return normalized as "en" | "es" | "pt";
  return DEFAULT_ATLAS_LANGUAGE;
};

export const getForcedAtlasSystemInstruction = (language: "en" | "es" | "pt") => {
  if (language === "en") {
    return "All outputs MUST be in English. Never mix languages. Do not use Portuguese or Spanish unless the selected language is explicitly changed to those languages.";
  }
  if (language === "es") {
    return "Todas las salidas DEBEN estar en español. Nunca mezcles idiomas. No uses portugués ni inglés a menos que el idioma seleccionado se cambie explícitamente a esos idiomas.";
  }
  return "Todas as saídas DEVEM estar em português. Nunca misture idiomas. Não use inglês ou espanhol a menos que o idioma selecionado seja alterado explicitamente para esses idiomas.";
};

type AtlasAiError = Error & {
  code?: string;
  details?: string;
  status?: number;
  debugCode?: string;
};

const ENGLISH_CLINICAL_MARKERS = [
  "slipped disc",
  "lower back",
  "broken kneecap",
  "fracture line",
  "lateral knee cyst",
  "herniated disc",
  "disc hernia",
  "transverse",
  "patellar",
  "displacement",
  "broken bone",
  "broken",
  "diagnosis",
  "findings",
  "technique",
  "plan",
];

const hasEnglishText = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    return ENGLISH_CLINICAL_MARKERS.some((marker) => normalized.includes(marker));
  }
  if (Array.isArray(value)) {
    return value.some((item) => hasEnglishText(item));
  }
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some((item) => hasEnglishText(item));
  }
  return false;
};

const hasLanguageMismatch = (value: unknown, language: "en" | "es" | "pt"): boolean => {
  const text = JSON.stringify(value || {}).toLowerCase();
  if (!text) return false;
  if (language === "en") {
    return /diagn[oó]stico|localiza[cç][aã]o|estrat[eé]gia|tratamento conservador|evid[eê]ncias cient[ií]ficas|materiais necess[aá]rios|p[oó]s-operat[oó]rio|recomenda[cç][oõ]es|t[eé]cnica 1|plano b/.test(text);
  }
  if (language === "es") {
    return /diagn[oó]stico|localiza[cç][ií]n|estrategia|tratamiento conservador|evidencia cient[ií]fica|materiales necesarios|postoperatorio|recomendaciones|t[eé]cnica 1|plan b/.test(text);
  }
  return hasEnglishText(value) || /diagnosis|location|strategy|conservative treatment|scientific evidence|required materials|postoperative|recommendations|technique 1|plan b/.test(text);
};

const forceAtlasLanguageRequest = (request: Record<string, unknown>): Record<string, unknown> => ({
  ...request,
  language: normalizeAtlasLanguage(request.language || request.selectedLanguage || request.locale || DEFAULT_ATLAS_LANGUAGE),
  selectedLanguage: normalizeAtlasLanguage(request.selectedLanguage || request.language || request.locale || DEFAULT_ATLAS_LANGUAGE),
  promptVersion: ATLAS_PROMPT_VERSION,
});

function makeAtlasAiError(message: string, code: string, details?: string, status?: number): AtlasAiError {
  const err = new Error(message) as AtlasAiError;
  err.code = code;
  err.debugCode = code;
  if (details) err.details = details;
  if (typeof status === "number") err.status = status;
  return err;
}

function normalizeAiError(error: any): AtlasAiError {
  const details = error?.details || {};
  const reason = String(details?.reason || error?.code || "").trim();
  const status = Number(details?.status ?? error?.status ?? 0) || undefined;
  const rawMessage = String(error?.message || details?.message || "").trim();
  const debugCode = String(details?.debugCode || error?.debugCode || "").trim();
  const isInputTooLarge =
    reason === "input_too_large" ||
    status === 413 ||
    debugCode === "ATLAS_PAYLOAD_TOO_LARGE" ||
    debugCode === "ATLAS_PLAN_PAYLOAD_TOO_LARGE";
  console.error("[AI][error raw]", error);

  if (isInputTooLarge) {
    const estimatedBytes = details?.estimatedBytes ?? details?.estimatedbytes ?? null;
    const maxBytes = details?.maxBytes ?? details?.maxbytes ?? null;
    const mimeType = details?.mimeType ?? details?.mimetype ?? null;
    const isPlanPayload = debugCode === "ATLAS_PLAN_PAYLOAD_TOO_LARGE";
    return makeAtlasAiError(
      isPlanPayload
        ? "Não foi possível gerar o plano. A imagem enviada ainda está pesada para análise textual."
        : "Imagem muito grande. Reduza a resolução ou envie outra imagem.",
      debugCode || "ATLAS_PAYLOAD_TOO_LARGE",
      (isPlanPayload ? rawMessage || "Entrada muito grande. Envie menos imagens ou imagens menores." : rawMessage) ||
        `O payload enviado pela imagem ou exame excedeu o limite esperado. reason=${reason || "unknown"} estimatedBytes=${estimatedBytes ?? "unknown"} maxBytes=${maxBytes ?? "unknown"} mimeType=${mimeType ?? "unknown"}`,
      status
    );
  }

  if (reason === "limit_exceeded" || error?.code === "functions/resource-exhausted" || status === 429) {
    return makeAtlasAiError(
      reason === "limit_exceeded"
        ? "Limite mensal de análises atingido."
        : "Limite temporário da IA atingido.",
      "ATLAS_RATE_LIMIT",
      rawMessage || "O limite mensal de análises foi atingido.",
      status
    );
  }

  if (reason === "unauthenticated" || error?.code === "functions/unauthenticated") {
    return makeAtlasAiError("Faça login para usar a IA.", "ATLAS_UNAUTHENTICATED", rawMessage, status);
  }

  if (reason === "plan_insufficient" || error?.code === "functions/permission-denied") {
    return makeAtlasAiError("Seu plano atual não permite este recurso de IA.", "ATLAS_PLAN_INSUFFICIENT", rawMessage, status);
  }

  if (reason === "backend_key_missing") {
    return makeAtlasAiError("IA não configurada no backend.", "ATLAS_BACKEND_KEY_MISSING", rawMessage, status);
  }

  if (reason === "invalid_payload" || reason === "invalid_base64" || error?.code === "functions/invalid-argument") {
    return makeAtlasAiError(
      rawMessage || "Não foi possível processar o arquivo enviado.",
      "ATLAS_INVALID_PAYLOAD",
      rawMessage || "O payload enviado pela imagem ou exame é inválido.",
      status
    );
  }

  if (reason === "timeout" || error?.code === "functions/deadline-exceeded" || error?.code === "deadline-exceeded") {
    return makeAtlasAiError(
      "A geração demorou demais. Tente novamente ou use confirmação manual.",
      "ATLAS_TIMEOUT",
      rawMessage || "A operação excedeu o tempo limite.",
      status
    );
  }

  if (reason === "atlas_low_confidence_blocked" || reason === "location_review_required") {
    return makeAtlasAiError(
      "Localização não confirmada. Selecione a região da lesão.",
      "ATLAS_LOW_CONFIDENCE_BLOCKED",
      rawMessage || "A localização da lesão precisa de confirmação manual.",
      status
    );
  }

  if (reason === "empty_response" || reason === "atlas_empty_response") {
    return makeAtlasAiError(
      "A IA retornou resposta vazia.",
      "ATLAS_IMAGE_EMPTY_RESPONSE",
      rawMessage || "Nenhum conteúdo foi retornado pelo modelo.",
      status
    );
  }

  if (reason === "json_parse_error" || reason === "atlas_json_parse_error") {
    return makeAtlasAiError(
      "Erro ao interpretar resposta da IA.",
      "ATLAS_JSON_PARSE_ERROR",
      rawMessage || "A resposta da IA não pôde ser convertida em JSON.",
      status
    );
  }

  if (reason === "ai_temporary_error" || error?.code === "functions/unavailable" || status === 503) {
    return makeAtlasAiError(
      "IA temporariamente indisponível.",
      "ATLAS_GEMINI_503",
      rawMessage || "O backend da IA retornou indisponibilidade temporária.",
      status
    );
  }

  return makeAtlasAiError(
    error?.message || "Falha ao chamar a IA.",
    "ATLAS_AI_GENERIC",
    rawMessage || JSON.stringify(details || error || {}),
    status
  );
}
export async function callAiFunction(route: AiRoute, request: Record<string, unknown>) {
  try {
    const callable = httpsCallable(functions, FUNCTION_BY_ROUTE[route]);
    const forcedRequest = forceAtlasLanguageRequest(request);
    if (route === "generateAtlasImage") {
      console.log("[ATLAS][generateAtlasImage][payload]", {
        keys: Object.keys(forcedRequest || {}),
        route: (forcedRequest as any)?.route || route,
        style: (forcedRequest as any)?.style || null,
        analysisMode: (forcedRequest as any)?.analysisMode || null,
        mimeType: (forcedRequest as any)?.mimeType,
        hasBase64: Boolean((forcedRequest as any)?.base64 || (forcedRequest as any)?.imageBase64 || (forcedRequest as any)?.imageData),
        base64Length: (forcedRequest as any)?.base64?.length || (forcedRequest as any)?.imageBase64?.length || (forcedRequest as any)?.imageData?.length,
        imageSize: (forcedRequest as any)?.imageSize,
        outputResolution: (forcedRequest as any)?.outputResolution,
        aspectRatio: (forcedRequest as any)?.aspectRatio,
        promptLength: (forcedRequest as any)?.prompt?.length,
      });
    }
    const result = await callable({ route, request: forcedRequest });
    const data = result.data as any;

    const shouldRetryLanguage = route !== "generateAtlasImage" && !forcedRequest[LANGUAGE_RETRY_DISABLED_FLAG];
    const requestedLanguage = normalizeAtlasLanguage(forcedRequest.language || forcedRequest.selectedLanguage || (forcedRequest as any)?.locale || DEFAULT_ATLAS_LANGUAGE);
    if (shouldRetryLanguage && !request[LANGUAGE_RETRY_FLAG] && hasLanguageMismatch(data, requestedLanguage)) {
      console.warn("[ATLAS_LANGUAGE_RETRY]", { route, language: requestedLanguage });
      const retryResult = await callable({
        route,
        request: {
          ...forcedRequest,
          [LANGUAGE_RETRY_FLAG]: true,
        },
      });
      const retryData = retryResult.data as any;
      if (hasLanguageMismatch(retryData, requestedLanguage)) {
        console.warn("[ATLAS_LANGUAGE_RETRY_FAILED]", { route, language: requestedLanguage });
        return retryData;
      }
      return retryData;
    }

    return data;
  } catch (error) {
    const rawMessage = String((error as any)?.message || (error as any)?.details?.message || "").toLowerCase();
    if (
      route === "generateAtlasImage" &&
      (
        rawMessage.includes("aspect ratio is not enabled") ||
        rawMessage.includes("models/gemini-2.5-flash") ||
        rawMessage.includes("model incompatible") ||
        rawMessage.includes("unsupported parameter")
      )
    ) {
      throw makeAtlasAiError(
        "Modelo de imagem incompatível. A geração visual precisa usar Gemini 3 Pro Image.",
        "ATLAS_IMAGE_MODEL_INCOMPATIBLE",
        String((error as any)?.message || (error as any)?.details?.message || "A configuração visual foi enviada para um modelo textual."),
        Number((error as any)?.status || (error as any)?.details?.status || 500) || 500
      );
    }
    if (
      route === "generateAtlasImage" &&
      (rawMessage.includes("timed out") || rawMessage.includes("deadline-exceeded") || rawMessage.includes("request timed out"))
    ) {
      throw makeAtlasAiError(
        "Reconstrução 3D ainda em andamento. Isso pode levar alguns minutos.",
        "ATLAS_TIMEOUT",
        String((error as any)?.message || (error as any)?.details?.message || "A geração visual ultrapassou o tempo esperado."),
        Number((error as any)?.status || (error as any)?.details?.status || 504) || 504
      );
    }
    throw normalizeAiError(error);
  }
}

export function createPrivateGenAI(defaultRoute: AiRoute = "generateContent") {
  return {
    models: {
      generateContent: (request: Record<string, unknown>) => callAiFunction(defaultRoute, request),
      generateVideos: async (_request?: Record<string, unknown>): Promise<any> => {
        throw new Error("Geração de vídeo por IA deve ser habilitada no backend antes de usar no cliente.");
      },
    },
    operations: {
      getVideosOperation: async (_request?: Record<string, unknown>): Promise<any> => {
        throw new Error("Geração de vídeo por IA deve ser habilitada no backend antes de usar no cliente.");
      },
    },
  };
}
